import { SampleOosPrediction } from "@/dev/sample-out-of-stock-predictions";
import { hotkeyHint } from "@/hooks/useCoverageBetaHotkeys";
import {
  selectRightPanelView,
  useCoverageBetaStore,
} from "@/store/useCoverageBetaStore";
import { cn } from "@/utils/utils";
import { OoSParcelSchema } from "@augmodo/lebron-browser-client";
import { IconLayoutGrid, IconPhoto } from "@tabler/icons-react";
import { useRef, useState, type PointerEvent } from "react";
import { CoverageBetaPlanogramView } from "./coverage-beta-planogram-view/CoverageBetaPlanogramView";
import {
  COVERAGE_BETA_RIGHT_FOOTER_HEIGHT,
  CoverageBetaRightFooter,
} from "./coverage-beta-right-footer/CoverageBetaRightFooter";
import { ShelfImagesView } from "./shelf-images-view/ShelfImagesView";

const HEADER_HEIGHT = 48;
const QA_FOOTER_DEFAULT_HEIGHT = 560;
const QA_FOOTER_MIN_HEIGHT = 220;
const QA_BODY_MIN_HEIGHT = 160;

interface Props {
  out_of_stock_predictions: SampleOosPrediction[];
  parcelResp: OoSParcelSchema | undefined;
  /** Forwarded to CoverageBetaPlanogramView — see its `isWarmupReady` prop. */
  isWarmupReady: boolean;
  /** Forwarded to ShelfImagesView — see its `onActiveReferenceLoaded` prop. */
  onActiveReferenceLoaded: () => void;
}

export const CoverageBetaRightPanel = (props: Props) => {
  const {
    out_of_stock_predictions,
    parcelResp,
    isWarmupReady,
    onActiveReferenceLoaded,
  } = props;

  // While confirming, grow the footer a bit (and shrink the image body) so the
  // candidate grid has room for the images + full product names.
  //
  // QA mode is different: its footer is user-draggable (some boxes need a
  // closer look at the image, others need more room for the panel), and
  // resizing the image/planogram body was what broke their pan/zoom —
  // react-zoom-pan-pinch's own ResizeObserver re-clamps (visibly resets) the
  // current pan position whenever its container's size changes, and dragging
  // the divider changed that size on every tick. So in QA mode the body never
  // resizes at all — it always fills the full height below the header — and
  // the footer instead floats on top of it as an absolutely-positioned
  // overlay, covering however much of the image the reviewer drags it over.
  const confirming = useCoverageBetaStore((s) => s.confirming);
  const isQaMode = useCoverageBetaStore((s) => s.isQaMode);
  const [qaFooterHeight, setQaFooterHeight] = useState(
    QA_FOOTER_DEFAULT_HEIGHT,
  );
  const footerHeight = confirming
    ? "260px"
    : `${COVERAGE_BETA_RIGHT_FOOTER_HEIGHT}px`;
  const BODY_HEIGHT = isQaMode
    ? `calc(100% - ${HEADER_HEIGHT}px)`
    : `calc(100% - ${HEADER_HEIGHT}px - ${footerHeight})`;
  // View lives in the store so the toggle-view hotkey can switch it. Tasks
  // without shelf images (pilot) are pinned to the planogram.
  const activeView = useCoverageBetaStore(selectRightPanelView);
  const hasShelfImages = useCoverageBetaStore((s) => s.hasShelfImages);
  const setActiveView = useCoverageBetaStore((s) => s.setRightPanelView);
  const recordInteraction = useCoverageBetaStore((s) => s.recordInteraction);

  // Divider drag (QA mode only) — same pointer-capture + start-position-ref
  // pattern as the box drag/resize handles in QueryEmptyAreaDetectionBox.
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startY: number; startHeight: number } | null>(null);

  const beginDividerDrag = (e: PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { startY: e.clientY, startHeight: qaFooterHeight };
  };

  const handleDividerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d) return;
    const containerHeight =
      containerRef.current?.getBoundingClientRect().height ?? 0;
    const maxHeight = Math.max(
      QA_FOOTER_MIN_HEIGHT,
      containerHeight - HEADER_HEIGHT - QA_BODY_MIN_HEIGHT,
    );
    // Dragging the handle up (clientY decreases) grows the footer.
    const next = d.startHeight + (d.startY - e.clientY);
    setQaFooterHeight(
      Math.min(maxHeight, Math.max(QA_FOOTER_MIN_HEIGHT, next)),
    );
  };

  const endDividerDrag = (e: PointerEvent<HTMLDivElement>) => {
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // pointer already released
    }
  };

  return (
    <div className="h-full relative" ref={containerRef}>
      {/* HEADER */}
      <div
        className="px-2 py-1.5"
        style={{ height: HEADER_HEIGHT }}
        data-cb-tour="right-view-toggle"
      >
        <div className="flex gap-4 h-full">
          {[
            {
              value: "shelf-images",
              label: "Shelf images",
              Icon: IconPhoto,
              disabled: false,
            },
            // {
            //   value: "summary",
            //   label: "Summary",
            //   Icon: IconList,
            //   disabled: false,
            // },
            {
              value: "planogram",
              label: "Planogram",
              Icon: IconLayoutGrid,
              disabled: false,
            },
          ]
            .filter(({ value }) => hasShelfImages || value !== "shelf-images")
            .map(({ value, label, Icon, disabled }) => {
              const isActive = value === activeView;
              return (
                <button
                  key={value}
                  className={cn(
                    "flex items-center justify-center h-full border-gray-300 border flex-1 rounded-lg gap-2  font-semibold text-sm cursor-pointer ",
                    isActive
                      ? "bg-blue-500/20 border-blue-500 text-blue-600"
                      : "hover:border-gray-500",
                    disabled && "bg-gray-300 opacity-50 cursor-not-allowed",
                  )}
                  onClick={() => {
                    if (disabled) return;
                    setActiveView(value as "shelf-images" | "planogram");
                    recordInteraction("toggle_view", "click");
                  }}
                >
                  <Icon size={16} />
                  <div>{label}</div>
                  {hasShelfImages && (
                    <kbd className="text-[10px] font-mono text-gray-400 border rounded px-1">
                      {hotkeyHint("toggle_view")}
                    </kbd>
                  )}
                </button>
              );
            })}
        </div>
      </div>

      {/* Both views stay mounted (so the planogram keeps its zoom/pan), but the
          inactive one is hidden with visibility:hidden — NOT display:none —
          overlapped via absolute positioning. display:none would give the
          zoom/pan container 0×0 dimensions, so PlanogramViewer's fitContent
          would compute against a zero size and jump when shown; visibility
          keeps real dimensions, so the transform stays put. */}
      {/* `isolate` so the body's whole subtree stacks as one unit beneath the
          QA footer overlay below (z-10). Without it a z-30 child of either
          view — the planogram's "not on this planogram" notice, say — competes
          directly with that overlay and paints over the QA panel, since none
          of the divs in between establish a stacking context. Mantine's
          popovers/hovercards/modals in here all render `withinPortal`, so they
          escape this context and are unaffected. */}
      <div
        style={{ height: BODY_HEIGHT }}
        className="relative isolate"
        data-cb-tour="right-panel-body"
      >
        {hasShelfImages && (
          <div
            className={cn(
              "absolute inset-0",
              activeView === "shelf-images"
                ? ""
                : "invisible pointer-events-none",
            )}
          >
            <ShelfImagesView
              out_of_stock_predictions={out_of_stock_predictions}
              parcelResp={parcelResp}
              onActiveReferenceLoaded={onActiveReferenceLoaded}
            />
          </div>
        )}

        <div
          className={cn(
            "absolute inset-0",
            activeView === "planogram" ? "" : "invisible pointer-events-none",
          )}
        >
          <CoverageBetaPlanogramView
            parcelResp={parcelResp}
            isActiveView={activeView === "planogram"}
            isWarmupReady={isWarmupReady}
          />
        </div>
      </div>

      {isQaMode ? (
        // QA mode: the panel floats over the (never-resized, see BODY_HEIGHT
        // above) image/planogram body instead of sharing flow space with it,
        // covering however much a reviewer drags it over. The drag handle is
        // its top edge, inside the same absolutely-positioned box, so
        // dragging only ever changes this overlay's own height — the body
        // behind it never resizes, so its pan/zoom is never disturbed.
        <div
          className="absolute left-0 right-0 bottom-0 z-10 flex flex-col bg-white shadow-[0_-2px_12px_rgba(0,0,0,0.12)]"
          style={{ height: qaFooterHeight }}
        >
          <div
            onPointerDown={beginDividerDrag}
            onPointerMove={handleDividerMove}
            onPointerUp={endDividerDrag}
            className="h-2 shrink-0 flex items-center justify-center cursor-row-resize bg-gray-100 hover:bg-gray-200 border-y border-gray-200"
            style={{ touchAction: "none" }}
          >
            <div className="w-10 h-1 rounded-full bg-gray-400" />
          </div>
          <div className="flex-1 min-h-0">
            <CoverageBetaRightFooter parcelResp={parcelResp} />
          </div>
        </div>
      ) : (
        /* FOOTER (expands while confirming — see footerHeight above) */
        <div style={{ height: footerHeight }} data-cb-tour="right-footer">
          <CoverageBetaRightFooter parcelResp={parcelResp} />
        </div>
      )}
    </div>
  );
};
